// @flow

import Bugsnag from '@bugsnag/js'
import { typeof navigate as Navigate } from 'gatsby'

import type { PricingPlan, State } from './types'

declare var process: WebProcess

export type AuthErrorLocationState = {
  +error: string,
}

// Redirect olden location states to Track auth.
export const redirectLegacyAuthCallbacks = () => {
  try {
    const url = new URL(window.location)

    if (!url.searchParams.has('state')) {
      return
    }

    const authUrl = new URL(
      window.location.search,
      process.env.GATSBY_TRACK_AUTH_URL
    ).href

    window.location.assign(authUrl)
  } catch (err) {
    Bugsnag.notify(err)
  }
}

// Handle `?authError` location state.
export const handleAuthErrors = (
  navigate: Navigate<AuthErrorLocationState>,
  pricingPlanUrls: Map<PricingPlan, string>
): any | void => {
  try {
    const url = new URL(window.location)

    if (!url.searchParams.has('authError')) {
      return
    }

    const authError = JSON.parse(atob(url.searchParams.get('authError') ?? ''))

    if (!(authError.message && authError.metaData)) {
      return Bugsnag.notify(
        new Error('Received auth erorr with missing data'),
        (event) => {
          event.addMetadata('Auth error', authError)
        }
      )
    }

    Bugsnag.notify(new Error(authError.message), (event) => {
      event.addMetadata('authError', authError.metaData)
    })

    const state: State = authError.metaData.state

    if (typeof state !== 'object') {
      return Bugsnag.notify('Received incorrect auth error state', (event) => {
        event.addMetadata('Auth error', authError)
      })
    }

    switch (state.type) {
      case 'apple-profile': {
        return
      }
      case 'profile': {
        return
      }
      case 'apple-sign-in': {
        const { fromTogglButton } = state

        if (fromTogglButton) {
          return navigate('/track/toggl-button-login/', {
            state: {
              error: authError.message,
            },
          })
        }

        return navigate('/track/login/', {
          state: {
            error: authError.message,
          },
        })
      }
      case 'apple-sign-up': {
        const { planId } = state
        const url = pricingPlanUrls.get(state.planId)

        if (!url) {
          Bugsnag.notify(
            new Error('Could not resolve signup URL for pricing plan'),
            (event) => {
              event.addMetadata('Plan ID', planId)
            }
          )

          return navigate('/track/signup/', {
            state: {
              error: authError.message,
            },
          })
        }

        return navigate(url)
      }
      case 'apple-sso-link': {
        return Bugsnag.notify(new Error('Not implemented'))
      }
      case 'google-sign-in': {
        const { fromTogglButton } = state

        if (fromTogglButton) {
          return navigate('/track/toggl-button-login/', {
            state: {
              error: authError.message,
            },
          })
        }

        return navigate('/track/login/', {
          state: {
            error: authError.message,
          },
        })
      }
      case 'google-sign-up': {
        const { planId } = state
        const url = pricingPlanUrls.get(state.planId)

        if (!url) {
          Bugsnag.notify(
            new Error('Could not resolve signup URL for pricing plan'),
            (event) => {
              event.addMetadata('Plan ID', planId)
            }
          )

          return navigate('/track/signup/', {
            state: {
              error: authError.message,
            },
          })
        }

        return navigate(url)
      }
      case 'google-sso-link': {
        return Bugsnag.notify(new Error('Not implemented'))
      }
      default:
        ;(state.type: empty)

        return Bugsnag.notify('Received unexpected state type', (event) => {
          event.addMetadata('State', state)
        })
    }
  } catch (err) {
    Bugsnag.notify(err)
  }
}
